import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Copy, Globe, Loader2, BarChart3, MousePointer, ShoppingCart, ImageDown, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const FLYER_SIZES = [
  { slug: 'classic', label: 'Classic 4:5' },
  { slug: 'square', label: 'Social Square' },
  { slug: 'story', label: 'Story / TikTok' },
  { slug: 'card', label: 'Business Card' },
];

// Partner Link Builder + per-domain performance for one campaign.
export const PartnerLinkTools = ({ campaign, token }) => {
  const [site, setSite] = useState('');
  const [built, setBuilt] = useState('');
  const [breakdown, setBreakdown] = useState(null);
  const [loadingBd, setLoadingBd] = useState(false);
  const [showBd, setShowBd] = useState(false);
  const [flyerBusy, setFlyerBusy] = useState(null);
  const [flyerPreview, setFlyerPreview] = useState(null);
  const [sharedDesigns, setSharedDesigns] = useState(null);

  useEffect(() => {
    axios.get(`${API}/promo/me/campaigns/${campaign.acceptance_id}/shared-designs`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSharedDesigns(r.data.designs || []))
      .catch(() => setSharedDesigns([]));
    // eslint-disable-next-line
  }, [campaign.acceptance_id]);

  const previewFlyer = async (domain, size = 'classic') => {
    setFlyerBusy(`${domain}-${size}`);
    try {
      const r = await axios.get(`${API}/promo/me/campaigns/${campaign.acceptance_id}/flyer`, {
        params: { domain, size }, responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      setFlyerPreview({ url: URL.createObjectURL(r.data), domain, size });
    } catch { toast.error('Could not build the flyer'); }
    finally { setFlyerBusy(null); }
  };

  const previewSharedDesign = async (d) => {
    setFlyerBusy(d.design_id);
    try {
      const r = await axios.get(`${API}/promo/me/campaigns/${campaign.acceptance_id}/shared-designs/${d.design_id}/flyer`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
      setFlyerPreview({ url: URL.createObjectURL(r.data), domain: '', size: d.name });
    } catch { toast.error('Could not build the flyer'); }
    finally { setFlyerBusy(null); }
  };

  const downloadFlyer = () => {
    if (!flyerPreview) return;
    const a = document.createElement('a');
    a.href = flyerPreview.url;
    a.download = `flyer-${flyerPreview.size || ''}-${flyerPreview.domain || 'eventism'}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('Flyer downloaded — post it anywhere, the QR does the tracking');
  };

  const buildLink = () => {
    let raw = site.trim();
    if (!raw) return;
    if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`;
    try {
      const u = new URL(raw);
      u.searchParams.set('p', campaign.tracking_code);
      setBuilt(u.toString());
    } catch {
      toast.error('That does not look like a valid website address');
    }
  };

  const copyBuilt = async () => {
    try { await navigator.clipboard.writeText(built); toast.success('Partner link copied'); }
    catch { toast.info(built); }
  };

  const toggleBreakdown = async () => {
    if (showBd) { setShowBd(false); return; }
    setShowBd(true);
    if (breakdown) return;
    setLoadingBd(true);
    try {
      const r = await axios.get(`${API}/promo/me/campaigns/${campaign.acceptance_id}/breakdown`, { headers: { Authorization: `Bearer ${token}` } });
      setBreakdown(r.data.breakdown || []);
    } catch { toast.error('Could not load breakdown'); }
    finally { setLoadingBd(false); }
  };

  return (
    <div className="space-y-2">
      {/* Organizer-provided partner sites → ready-made links */}
      {(campaign.partner_domains || []).length > 0 && (
        <div className="bg-muted/40 rounded p-2 space-y-1.5" data-testid={`partner-ready-links-${campaign.event_id}`}>
          <p className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
            <Globe className="h-3 w-3" /> Ready-made partner links
            <span className="normal-case font-normal text-muted-foreground/70">— the organizer's partner sites, your tracking built in</span>
          </p>
          {campaign.partner_domains.map(d => {
            const url = `https://${d}/?p=${campaign.tracking_code}`;
            return (
              <div key={d} className="flex items-center gap-2" data-testid={`ready-link-${d}`}>
                <Input value={url} readOnly className="text-xs font-mono h-8 text-emerald-500" />
                <Button size="sm" variant="outline" className="h-8" onClick={async () => { try { await navigator.clipboard.writeText(url); toast.success(`${d} link copied`); } catch { toast.info(url); } }} data-testid={`ready-link-copy-${d}`}><Copy className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" className="h-8 whitespace-nowrap" onClick={() => previewFlyer(d)} disabled={flyerBusy === `${d}-classic`} title="Preview a flyer with a QR code for this link" data-testid={`ready-link-flyer-${d}`}>
                  {flyerBusy === `${d}-classic` ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageDown className="h-3 w-3" />}<span className="ml-1 hidden sm:inline">QR Flyer</span>
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* QR Flyers — pick a size, QR carries your tracking link */}
      <div className="bg-muted/40 rounded p-2 space-y-1.5" data-testid={`qr-flyers-${campaign.event_id}`}>
        <p className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
          <ImageDown className="h-3 w-3" /> QR Flyers
          <span className="normal-case font-normal text-muted-foreground/70">— every size, your tracked QR built in</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {FLYER_SIZES.map(s => (
            <Button key={s.slug} size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap"
              onClick={() => previewFlyer('', s.slug)} disabled={flyerBusy === `-${s.slug}`}
              data-testid={`qr-flyer-${s.slug}`}>
              {flyerBusy === `-${s.slug}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageDown className="h-3 w-3 mr-1" />}
              {s.label}
            </Button>
          ))}
        </div>
        {(sharedDesigns || []).length > 0 && (
          <div className="pt-1 space-y-1" data-testid={`shared-flyers-${campaign.event_id}`}>
            <p className="text-[10px] uppercase font-semibold text-fuchsia-400 flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> From the organizer
              <span className="normal-case font-normal text-muted-foreground/70">— custom flyers made for this event, your QR inside</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {sharedDesigns.map(d => (
                <Button key={d.design_id} size="sm" variant="outline"
                  className="h-8 text-xs whitespace-nowrap border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
                  onClick={() => previewSharedDesign(d)} disabled={flyerBusy === d.design_id}
                  data-testid={`shared-flyer-${d.design_id}`}>
                  {flyerBusy === d.design_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  {d.name}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Link builder */}
      <div className="bg-muted/40 rounded p-2 space-y-1.5" data-testid={`link-builder-${campaign.event_id}`}>
        <p className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
          <Globe className="h-3 w-3" /> Partner Link Builder
          <span className="normal-case font-normal text-muted-foreground/70">— {campaign.partner_domains?.length ? 'or promote any other site with this event\'s widget' : 'promote any site that has this event\'s ticket widget'}</span>
        </p>
        <div className="flex items-center gap-2">
          <Input
            value={site}
            onChange={(e) => setSite(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && buildLink()}
            placeholder="e.g. nocturnalhalloween.com"
            className="text-xs h-8"
            data-testid={`link-builder-input-${campaign.event_id}`}
          />
          <Button size="sm" variant="outline" className="h-8 whitespace-nowrap" onClick={buildLink} data-testid={`link-builder-build-${campaign.event_id}`}>Build link</Button>
        </div>
        {built && (
          <div className="flex items-center gap-2">
            <Input value={built} readOnly className="text-xs font-mono h-8 text-emerald-500" data-testid={`link-builder-output-${campaign.event_id}`} />
            <Button size="sm" variant="outline" className="h-8" onClick={copyBuilt} data-testid={`link-builder-copy-${campaign.event_id}`}><Copy className="h-3 w-3" /></Button>
          </div>
        )}
      </div>

      {/* Flyer preview modal */}
      <Dialog open={!!flyerPreview} onOpenChange={(o) => { if (!o && flyerPreview) { URL.revokeObjectURL(flyerPreview.url); setFlyerPreview(null); } }}>
        <DialogContent className="max-w-md" data-testid="flyer-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Flyer preview — QR goes to {flyerPreview?.domain || 'your Eventism link'}</DialogTitle>
          </DialogHeader>
          {flyerPreview && (
            <>
              <img src={flyerPreview.url} alt="Event flyer preview" className="w-full rounded-lg border border-border" data-testid="flyer-preview-img" />
              <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" onClick={downloadFlyer} data-testid="flyer-download-btn">
                <ImageDown className="h-4 w-4 mr-2" /> Download flyer
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-domain breakdown */}
      <button type="button" onClick={toggleBreakdown} className="text-[11px] text-indigo-400 hover:text-indigo-300 flex items-center gap-1" data-testid={`breakdown-toggle-${campaign.event_id}`}>
        <BarChart3 className="h-3 w-3" /> {showBd ? 'Hide' : 'Where your sales come from'}
      </button>
      {showBd && (
        <div className="border border-border/60 rounded p-2" data-testid={`breakdown-panel-${campaign.event_id}`}>
          {loadingBd ? <Loader2 className="h-4 w-4 animate-spin text-indigo-400 mx-auto my-2" /> :
            (breakdown || []).length === 0 ? (
              <p className="text-[11px] text-muted-foreground text-center py-1">No clicks or sales tracked yet — share your links!</p>
            ) : (
              <div className="space-y-1">
                {breakdown.map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] border-b border-border/40 last:border-0 pb-1 last:pb-0" data-testid={`breakdown-row-${campaign.event_id}-${i}`}>
                    <span className="font-medium truncate max-w-[45%]">{b.domain}</span>
                    <span className="flex items-center gap-3 text-muted-foreground">
                      <span><MousePointer className="h-3 w-3 inline mr-0.5" />{b.clicks}</span>
                      <span><ShoppingCart className="h-3 w-3 inline mr-0.5" />{b.orders}</span>
                      <span className="text-emerald-500 font-semibold font-mono">{fmt(b.revenue_cents)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default PartnerLinkTools;
